import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChargingUserDownloadReportDialogComponent } from 'src/app/features/charging-users/charging-user-download-report-dialog/charging-user-download-report-dialog.component';
import { CreateDepartmentDialogComponent } from 'src/app/features/department/components/create-update-department-dialog/create-update-department-dialog.component';
import { CreateDepartmentDialogData } from 'src/app/features/department/department.model';
import { OrganizationDisplay } from '../../organization-models';
import { OrganizationListStateService } from './organization-list-state.service';
import { CreateUserDialogComponent } from '../create-user-dialog/create-user-dialog.component';
import OrganizationService from 'src/app/shared/services/organization.service';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { catchError, combineLatest, filter, of, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { SnackbarService } from 'ok-angular-shared/components';
import { OrganizationType } from 'src/app/shared/enums/organization-type.enum';
import {
  AccessGroupDefaultModalComponent,
  AccessGroupDefaultModalComponentData,
} from 'src/app/features/access-groups/access-group-default-modal/access-group-default-modal.component';
import { EditOrganizationDialogComponent } from '../edit-organization-dialog/edit-organization-dialog.component';
import { DepartmentGroupAddOrEditComponent } from 'src/app/features/department/components/department-group-add-or-edit/department-group-add-or-edit.component';
import { DownloadReportDialogComponent } from '../download-report-dialog/download-report-dialog.component';
import { UpdateDiscountTypeComponent, UpdateDiscountTypeData } from '../update-discount-type-dialog/update-discount-type-dialog.component';
import { KickbackModalComponent, KickbackModalData } from '../kickback-modal/kickback-modal.component';
import { ImitateOrganizationModalComponent } from '../imitate-organization-modal/imitate-organization-modal.component';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

export class OrganizationListDialogService {
  private dialog = inject(MatDialog);
  private state = inject(OrganizationListStateService);
  private organizationService = inject(OrganizationService);
  private snackbarService = inject(SnackbarService);

  constructor() {
    combineLatest([toObservable(this.state.filters), toObservable(this.state.focusedOrganization)])
      .pipe(
        filter(([filters, focusedOrg]) => filters.kickbackId != null && focusedOrg != null && focusedOrg.id != null),
        tap(([_, focusedOrg]) => {
          this.openKickbackModal({ id: focusedOrg!.id, name: focusedOrg!.name } as OrganizationDisplay);
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  public openDownloadReportDialog() {
    this.dialog.open(ChargingUserDownloadReportDialogComponent, {
      width: '600px',
    });
  }

  public openCreateDepartmentDialog(org: OrganizationDisplay) {
    const dialogRef = this.dialog.open(CreateDepartmentDialogComponent, {
      width: '480px',
      data: <CreateDepartmentDialogData>{
        organizationId: org.id,
        organizationName: org.name,
        organizationType: org.organizationType,
      },
    });
    dialogRef.afterClosed().subscribe(departmentCreated => {
      if (departmentCreated) {
        this.state.highlightDepartmentId = departmentCreated.departmentId;

        this.state.refreshSingleOrganization$.next(org.id);
      }
    });
  }
  public showCreateUserMenu(organization: OrganizationDisplay) {
    this.dialog.open(CreateUserDialogComponent, {
      data: {
        organization: organization,
      },
      width: '450px',
    });
  }

  public openDeleteOrganizationDialog(organization: OrganizationDisplay) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Slet organisation`,
        text: `Er du sikker på at du vil slette organisationen ${organization.name}?`,
        confirmText: `Slet`,
        denyText: `Luk`,
        icon: { name: 'trash-s' },
      },
      disableClose: true,
    });
    confirmDialogRef.afterClosed().subscribe(async confirmed => {
      if (confirmed) {
        this.organizationService
          .deleteOrganization(organization.id)
          .pipe(
            catchError((httpError: HttpErrorResponse) => {
              if (httpError.status == 404 && httpError.error) {
                const error = httpError.error as ErrorMessage;
                if (error.code === 100012) {
                  this.snackbarService.warning(`Organisationen kunne ikke findes.`);
                }
              } else if (httpError.status == 400 && httpError.error) {
                const error = httpError.error as ErrorMessage;
                if (error.code === 100014) {
                  this.snackbarService.warning(`Organisationen kunne ikke slettes da den har afdelinger.`);
                }
              } else {
                this.snackbarService.error('Der skete en ukendt fejl under sletning af organisation');
              }
              return of();
            })
          )
          .subscribe(() => {
            this.snackbarService.success(`Organisationen blev slettet!`);
            this.state.refresh$.next();
          });
      }
    });
  }

  public openDefaultAccessGroupModal(organizationType: OrganizationType) {
    this.dialog.open(AccessGroupDefaultModalComponent, {
      width: '940px',
      data: <AccessGroupDefaultModalComponentData>{
        organizationType,
      },
    });
  }

  public openEditOrganizationDialog(organization: OrganizationDisplay) {
    const dialogRef = this.dialog.open(EditOrganizationDialogComponent, {
      width: '400px',
      data: { organizationId: organization.id },
    });

    dialogRef.afterClosed().subscribe(organizationUpdated => {
      if (organizationUpdated) {
        this.state.refreshSingleOrganization$.next(organization.id);
      }
    });
  }

  public openOrganizationDownloadReportDialog(organization: OrganizationDisplay) {
    this.dialog.open(DownloadReportDialogComponent, {
      width: '600px',
      data: { organizationId: organization.id, organizationName: organization.name },
    });
  }

  public openEditDiscountTypeModal(organization: OrganizationDisplay) {
    const data: UpdateDiscountTypeData = {
      organizationId: organization.id,
      organizationName: organization.name,
      existingDiscountType: organization.discountType,
    };

    this.dialog
      .open(UpdateDiscountTypeComponent, {
        data: data,
        width: '850px',
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.state.refreshSingleOrganization$.next(organization.id);
        }
      });
  }

  public openDepartmentGroupAddOrEditDialog(organization: OrganizationDisplay) {
    const dialogRef = this.dialog.open(DepartmentGroupAddOrEditComponent, {
      data: { organizationId: organization.id },
    });

    dialogRef.afterClosed().subscribe(organizationUpdated => {
      if (organizationUpdated) {
        this.state.refreshSingleOrganization$.next(organization.id);
      }
    });
  }
  public imitateOrganization(organization: OrganizationDisplay) {
    this.dialog.open(ImitateOrganizationModalComponent, {
      width: '600px',
      data: { organization: organization },
    });
  }

  public openKickbackModal(organization: OrganizationDisplay) {
    this.dialog.open(KickbackModalComponent, {
      width: '900px',
      data: <KickbackModalData>{
        organizationId: organization.id,
        organizationName: organization.name,
      },
    });
  }
}
